import { useState, useEffect, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Card,
  Table,
  Button,
  Space,
  Select,
  Tag,
  Modal,
  Form,
  Input,
  DatePicker,
  Statistic,
  Row,
  Col,
  Progress,
  message,
  Alert,
  Tabs,
  Badge,
} from 'antd'
import type { TableProps } from 'antd'
import {
  SendOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  MailOutlined,
  TwitterOutlined,
  EyeOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  RocketOutlined,
  RetweetOutlined,
  HistoryOutlined,
  AppstoreOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import {
  useOutreachRecords,
  useQueueStatus,
  useCreateOutreachTask,
  useRetryOutreach,
  useCancelOutreach,
  useSendOutreach,
  useProjects,
  useReplies,
  useUnprocessedRepliesCount,
  useMarkReplyProcessed,
} from '../hooks'
import { outreachApi, replyApi, scrapeApi, accountApi } from '../api/client'
import { useOutreachFilterStore } from '../store'
import type { OutreachRecord, OutreachChannel, OutreachStatus, Reply } from '../types'
import MissingInfoModal from '../components/MissingInfoModal'
import ProjectInfoDrawer from '../components/ProjectInfoDrawer'

const { RangePicker } = DatePicker

const channelConfig: Record<OutreachChannel, { icon: React.ReactNode; color: string; text: string }> = {
  email: { icon: <MailOutlined />, color: 'blue', text: 'Email' },
  twitter_dm: { icon: <TwitterOutlined />, color: 'cyan', text: 'Twitter DM' },
  twitter_reply: { icon: <TwitterOutlined />, color: 'geekblue', text: 'Twitter Reply' },
  telegram_group: { icon: <SendOutlined />, color: 'purple', text: 'TG 群消息' },
  telegram_dm: { icon: <SendOutlined />, color: 'purple', text: 'TG 群主私信' },
  telegram_admin_dm: { icon: <SendOutlined />, color: 'purple', text: 'TG 管理员私信' },
  discord: { icon: <SendOutlined />, color: 'magenta', text: 'Discord' },
}

const statusConfig: Record<OutreachStatus, { color: string; text: string }> = {
  pending: { color: 'default', text: '待发送' },
  sent: { color: 'processing', text: '已发送' },
  delivered: { color: 'cyan', text: '已送达' },
  opened: { color: 'blue', text: '已打开' },
  clicked: { color: 'purple', text: '已点击' },
  replied: { color: 'success', text: '已回复' },
  bounced: { color: 'warning', text: '退信' },
  failed: { color: 'error', text: '失败' },
  cooling_down: { color: 'default', text: '冷却中' },
}

function OutreachCenter() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [taskModalOpen, setTaskModalOpen] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<OutreachRecord | null>(null)
  const [missingInfoModalOpen, setMissingInfoModalOpen] = useState(false)
  const [missingInfoData, setMissingInfoData] = useState<any[]>([])
  const [form] = Form.useForm()
  const [batchScraping, setBatchScraping] = useState(false)
  const [selectedRecordKeys, setSelectedRecordKeys] = useState<React.Key[]>([])
  const [projectInfoDrawerOpen, setProjectInfoDrawerOpen] = useState(false)
  const [selectedProjectForInfo, setSelectedProjectForInfo] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('outreach')
  const [strategyModalOpen, setStrategyModalOpen] = useState(false)
  const [timelineDrawerOpen, setTimelineDrawerOpen] = useState(false)
  const [timelineProjectId, setTimelineProjectId] = useState<string | null>(null)
  const [createAndSendModalOpen, setCreateAndSendModalOpen] = useState(false)
  const [createAndSendForm] = Form.useForm()

  // 项目触达视图
  const [prjChannel, setPrjChannel] = useState<string>('email')
  const [prjSendStatus, setPrjSendStatus] = useState<string>('unsent')
  const [prjInfoComplete, setPrjInfoComplete] = useState<string>('complete')
  const [prjSearch, setPrjSearch] = useState('')
  const [prjPage, setPrjPage] = useState(1)
  const [prjSelectedKeys, setPrjSelectedKeys] = useState<React.Key[]>([])
  const [prjData, setPrjData] = useState<{ items: any[]; total: number }>({ items: [], total: 0 })
  const [prjLoading, setPrjLoading] = useState(false)
  const [prjSending, setPrjSending] = useState(false)

  const loadOutreachProjects = async () => {
    setPrjLoading(true)
    try {
      const data = await outreachApi.getProjects({
        channel: prjChannel,
        send_status: prjSendStatus,
        info_complete: prjInfoComplete,
        search: prjSearch || undefined,
        page: prjPage,
        page_size: 50,
      })
      setPrjData({ items: data.items || [], total: data.total || 0 })
    } catch { setPrjData({ items: [], total: 0 }) }
    setPrjLoading(false)
  }

  useEffect(() => { loadOutreachProjects() }, [prjChannel, prjSendStatus, prjInfoComplete, prjPage]) // eslint-disable-line

  const handleBatchSendProjects = async () => {
    if (prjSelectedKeys.length === 0) { message.warning('请先选择项目'); return }
    setPrjSending(true)
    try {
      const result = await outreachApi.createAndSend({
        project_ids: prjSelectedKeys.map(String),
        channel: prjChannel,
      })
      message.success(`已提交 ${result.sent + result.queued} 个触达任务`)
      setPrjSelectedKeys([])
      loadOutreachProjects()
      refetch()
    } catch (e: any) {
      message.error(`发送失败: ${e.message}`)
    }
    setPrjSending(false)
  }

  // 回复相关
  const { data: repliesData, isLoading: repliesLoading, refetch: refetchReplies } = useReplies({ page_size: 20 })
  const { data: unprocessedCount } = useUnprocessedRepliesCount()
  const markProcessed = useMarkReplyProcessed()

  const [twitterStatus, setTwitterStatus] = useState<{
    configured: boolean
    authenticated: boolean
    rate_limited: boolean
    rate_limit_remaining_seconds: number
    error: string | null
  } | null>(null)

  // 定期检查 Twitter API 状态
  const checkTwitterStatus = useCallback(async () => {
    try {
      const status = await accountApi.getTwitterApiStatus()
      setTwitterStatus(status)
    } catch {
      // 接口不可用时静默忽略
    }
  }, [])

  useEffect(() => {
    checkTwitterStatus()
    const timer = setInterval(checkTwitterStatus, 60000) // 每分钟刷新
    return () => clearInterval(timer)
  }, [checkTwitterStatus])

  // 从URL获取参数
  const projectIdFromUrl = searchParams.get('project_id')
  const projectIdsFromUrl = searchParams.get('project_ids')

  // 当URL有项目参数时，自动打开创建任务模态框
  useEffect(() => {
    if (projectIdFromUrl || projectIdsFromUrl) {
      setTaskModalOpen(true)
      // 预填充表单
      const ids = projectIdFromUrl
        ? [projectIdFromUrl]  // 单个项目ID
        : projectIdsFromUrl?.split(',') || []  // 批量项目IDs
      form.setFieldsValue({
        project_ids: ids,
        channel: 'email',
        phase: 1,
      })
    }
  }, [projectIdFromUrl, projectIdsFromUrl, form])

  // 状态管理
  const { channel, status, infoFilter, page, pageSize, setFilter } = useOutreachFilterStore()

  // 数据查询 — 同步新筛选器到触达记录查询
  const effectiveChannel = prjChannel || channel || undefined
  const effectiveStatus = prjSendStatus === 'sent' ? 'sent' : prjSendStatus === 'failed' ? 'failed' : (status || undefined)
  const { data, isLoading, refetch } = useOutreachRecords({
    channel: effectiveChannel,
    status: effectiveStatus,
    info_filter: infoFilter || undefined,
    project_id: projectIdFromUrl || undefined,
    page,
    page_size: pageSize,
  })
  const { data: queueStatus, refetch: refetchQueueStatus } = useQueueStatus()
  const [etQueueStatus, setEtQueueStatus] = useState<any>(null)
  const { data: projectsData } = useProjects({ page_size: 100 })

  // 加载 Email/Telegram 队列状态
  const loadEtQueue = async () => {
    try {
      const status = await accountApi.getOutreachQueueStatus()
      setEtQueueStatus(status)
    } catch { /* ignore */ }
  }
  useEffect(() => { loadEtQueue() }, [])  // eslint-disable-line

  // Mutations
  const createTask = useCreateOutreachTask()
  const retryOutreach = useRetryOutreach()
  const cancelOutreach = useCancelOutreach()
  const sendOutreach = useSendOutreach()

  // 表格列定义
  const columns: TableProps<OutreachRecord>['columns'] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '项目',
      dataIndex: 'project_id',
      key: 'project_id',
      width: 150,
      render: (projectId: string, record: OutreachRecord) => (
        <Space size={4}>
          <a onClick={() => navigate(`/projects/${projectId}`)}>
            {record.project?.name || `项目 ${projectId}`}
          </a>
          <Button type="link" size="small" icon={<HistoryOutlined />} onClick={() => openProjectTimeline(String(projectId))} title="时间线" />
        </Space>
      ),
    },
    {
      title: '渠道',
      dataIndex: 'channel',
      key: 'channel',
      width: 120,
      render: (channel: OutreachChannel) => {
        const config = channelConfig[channel]
        return (
          <Tag icon={config?.icon} color={config?.color}>
            {config?.text || channel}
          </Tag>
        )
      },
    },
    {
      title: '阶段',
      dataIndex: 'channel',
      key: 'phase',
      width: 80,
      render: (channel: string) => {
        const phaseMap: Record<string, number> = {
          email: 1, twitter_dm: 2, twitter_reply: 2,
          telegram_group: 3, telegram_dm: 3, telegram_admin_dm: 3,
        }
        const phase = phaseMap[channel] ?? '-'
        return `Phase ${phase}`
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: OutreachStatus) => {
        const config = statusConfig[status]
        return <Tag color={config?.color}>{config?.text || status}</Tag>
      },
    },
    {
      title: '接收方',
      key: 'recipient',
      width: 160,
      ellipsis: true,
      render: (_: any, record: OutreachRecord) => {
        const ch = record.channel
        const proj = record.project as any
        if (ch === 'email') return proj?.email || '-'
        if (ch === 'twitter_dm' || ch === 'twitter_reply') return proj?.twitter_handle ? `@${proj.twitter_handle}` : '-'
        if (ch === 'telegram_group') return proj?.telegram_group ? (
          <a href={proj.telegram_group} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>
            {proj.telegram_group.replace('https://t.me/', 't.me/')}
          </a>
        ) : '-'
        if (ch === 'telegram_admin_dm' || ch === 'telegram_dm') {
          const admins = proj?.telegram_admins || []
          if (admins.length === 0) return <span style={{ color: '#999' }}>无管理员</span>
          return (
            <span>
              {admins.slice(0, 2).map((a: any, i: number) => (
                <Tag key={i} color="purple" style={{ fontSize: 11 }}>@{a.username || '匿名'}</Tag>
              ))}
              {admins.length > 2 && <span style={{ fontSize: 11, color: '#999' }}>+{admins.length - 2}</span>}
            </span>
          )
        }
        return '-'
      },
    },
    {
      title: '主题/内容',
      key: 'summary',
      width: 200,
      ellipsis: true,
      render: (_: any, record: OutreachRecord) => {
        if (record.subject) return record.subject
        if ((record as any).content_preview) return (record as any).content_preview
        if (record.content) return record.content.length > 100 ? record.content.slice(0, 100) + '...' : record.content
        return '-'
      },
    },
    {
      title: '发送时间',
      dataIndex: 'sent_at',
      key: 'sent_at',
      width: 150,
      render: (time: string) => (time ? dayjs(time).format('MM-DD HH:mm') : '-'),
    },
    {
      title: '打开时间',
      dataIndex: 'opened_at',
      key: 'opened_at',
      width: 150,
      render: (time: string) => (time ? dayjs(time).format('MM-DD HH:mm') : '-'),
    },
    {
      title: '回复时间',
      dataIndex: 'replied_at',
      key: 'replied_at',
      width: 150,
      render: (time: string) => (time ? dayjs(time).format('MM-DD HH:mm') : '-'),
    },
    {
      title: '重试',
      dataIndex: 'retry_count',
      key: 'retry_count',
      width: 60,
      render: (count: number) => count || 0,
    },
    {
      title: '操作',
      key: 'action',
      width: 230,
      render: (_: unknown, record: OutreachRecord) => (
        <Space size="small">
          {/* 查看项目详情按钮 */}
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedProjectForInfo(record.project_id)
              setProjectInfoDrawerOpen(true)
            }}
          >
            查看项目
          </Button>
          {/* 查看触达详情按钮 - 所有状态都显示 */}
          <Button
            type="link"
            size="small"
            onClick={() => {
              setSelectedRecord(record)
              setDetailModalOpen(true)
            }}
          >
            触达详情
          </Button>

          {/* 待发送状态: 发送和取消按钮 */}
          {record.status === 'pending' && (
            <>
              <Button
                type="link"
                size="small"
                icon={<SendOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: '确认发送',
                    icon: <ExclamationCircleOutlined />,
                    content: `确定要立即发送给 ${record.project?.name || '该项目'} 吗？`,
                    okText: '发送',
                    cancelText: '取消',
                    onOk: () => sendOutreach.mutate({
                      record_id: record.id,
                    }),
                  })
                }}
              >
                发送
              </Button>
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: '确认取消',
                    icon: <ExclamationCircleOutlined />,
                    content: '确定要取消这条触达任务吗？取消后不可恢复。',
                    okText: '确认取消',
                    okType: 'danger',
                    cancelText: '返回',
                    onOk: () => cancelOutreach.mutate(record.id),
                  })
                }}
              >
                取消
              </Button>
            </>
          )}

          {/* 失败状态: 重试和取消按钮 */}
          {record.status === 'failed' && (
            <>
              <Button
                type="link"
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => retryOutreach.mutate(record.id)}
              >
                重试
              </Button>
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: '确认取消',
                    icon: <ExclamationCircleOutlined />,
                    content: '确定要取消这条失败的触达记录吗？',
                    okText: '确认取消',
                    okType: 'danger',
                    cancelText: '返回',
                    onOk: () => cancelOutreach.mutate(record.id),
                  })
                }}
              >
                删除
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ]

  // 获取信息完整性筛选选项（根据渠道动态调整）
  const getInfoFilterOptions = () => {
    if (!channel) {
      return [
        { value: 'has_missing', label: '存在缺失' },
        { value: 'all_complete', label: '信息完整' },
      ]
    }

    const channelTypeMap: Record<string, string> = {
      email: '邮箱',
      twitter_dm: '推特',
      twitter_reply: '推特',
      telegram_group: 'Telegram',
      telegram_dm: 'Telegram',
      telegram_admin_dm: 'Telegram',
    }

    const type = channelTypeMap[channel] || '信息'
    return [
      { value: 'missing', label: `${type}缺失` },
      { value: 'complete', label: `${type}完整` },
    ]
  }

  // 批量抓取选中记录的缺失信息
  const handleBatchScrapeFromRecords = async () => {
    if (selectedRecordKeys.length === 0) {
      message.warning('请先选择触达记录')
      return
    }

    // 从选中的记录中提取项目ID和需要的信息类型
    const selectedRecords = (data?.items || []).filter(r => selectedRecordKeys.includes(r.id))
    const projectInfoMap = new Map<string, Set<string>>()

    selectedRecords.forEach(record => {
      const { project_id, channel: recordChannel } = record
      if (!projectInfoMap.has(project_id)) {
        projectInfoMap.set(project_id, new Set())
      }

      // 根据渠道确定需要的信息类型
      if (recordChannel === 'email') {
        projectInfoMap.get(project_id)!.add('email')
      } else if (recordChannel === 'twitter_dm' || recordChannel === 'twitter_reply') {
        projectInfoMap.get(project_id)!.add('twitter')
      } else if (recordChannel === 'telegram_group' || recordChannel === 'telegram_dm' || recordChannel === 'telegram_admin_dm') {
        projectInfoMap.get(project_id)!.add('telegram')
      }
    })

    const projectIds = Array.from(projectInfoMap.keys())
    const allTypes = new Set<string>()
    projectInfoMap.forEach(types => {
      types.forEach(t => allTypes.add(t))
    })

    Modal.confirm({
      title: '批量抓取确认',
      content: (
        <div>
          <p>将为 <strong>{projectIds.length}</strong> 个项目抓取以下信息：</p>
          <Space>
            {Array.from(allTypes).map(type => (
              <Tag key={type} color="processing">
                {type === 'email' ? '邮箱' : type === 'twitter' ? '推特' : 'Telegram'}
              </Tag>
            ))}
          </Space>
        </div>
      ),
      onOk: async () => {
        setBatchScraping(true)
        try {
          const result = await scrapeApi.batchScrape({
            project_ids: projectIds,
            scrape_types: Array.from(allTypes),
            force: false,
          })

          const successCount = result.success || 0
          const failedCount = result.failed || 0

          if (failedCount > 0) {
            message.warning(`抓取完成！成功: ${successCount}, 失败: ${failedCount}`)
          } else {
            message.success(`抓取完成！成功抓取 ${successCount} 个项目`)
          }

          refetch()
          setSelectedRecordKeys([])
        } catch (error: any) {
          message.error(`抓取失败: ${error.message || '未知错误'}`)
        } finally {
          setBatchScraping(false)
        }
      },
    })
  }

  // 暂停/恢复队列
  const handleTogglePause = async () => {
    try {
      if (isPaused) {
        await outreachApi.resumeQueue()
        message.success('队列已恢复')
      } else {
        await outreachApi.pauseQueue()
        message.success('队列已暂停')
      }
      setIsPaused(!isPaused)
    } catch (error) {
      message.error('操作失败')
    }
  }

  // 创建触达任务
  const handleCreateTask = async (values: {
    project_ids: string[]  // UUID字符串数组
    channel: OutreachChannel
    phase?: number
  }) => {
    try {
      const result = await createTask.mutateAsync({
        project_ids: values.project_ids,
        channel: values.channel,
        phase: values.phase,
      })

      // 检查是否有需要抓取的项目
      if (result.needs_scraping && result.needs_scraping.length > 0) {
        setMissingInfoData(result.needs_scraping)
        setMissingInfoModalOpen(true)
        setTaskModalOpen(false)
        form.resetFields()
      } else if (result.success > 0) {
        message.success(`任务创建成功：${result.success} 个项目`)
        setTaskModalOpen(false)
        form.resetFields()
      }
    } catch (error) {
      // 错误已在API层处理
    }
  }

  // P0-1: 一步式创建并发送
  const handleCreateAndSend = async (values: { project_ids: string[]; channel: string }) => {
    try {
      const result = await outreachApi.createAndSend({
        project_ids: values.project_ids,
        channel: values.channel,
      })
      const parts = [`成功发送 ${result.sent}`]
      if (result.failed > 0) parts.push(`失败 ${result.failed}`)
      if (result.skipped > 0) parts.push(`跳过 ${result.skipped}`)
      message.success(parts.join('，'))
      setCreateAndSendModalOpen(false)
      createAndSendForm.resetFields()
      refetch()
    } catch (error: any) {
      message.error(`发送失败: ${error.message || '未知错误'}`)
    }
  }

  // P1-3: 一键重试所有失败
  const handleRetryAllFailed = async () => {
    Modal.confirm({
      title: '一键重试所有失败',
      icon: <ExclamationCircleOutlined />,
      content: '将所有失败的触达记录重置为待发送状态，等待自动重试。确认？',
      okText: '确认重试',
      cancelText: '取消',
      onOk: async () => {
        try {
          const result = await outreachApi.retryAllFailed()
          message.success(result.message)
          refetch()
        } catch (error: any) {
          message.error(`操作失败: ${error.message}`)
        }
      },
    })
  }

  // P1-1: 应用策略
  const handleApplyStrategy = async (params: { strategy: string; channels: string[]; intervals: Record<string, number> }) => {
    const projectIds = createAndSendForm.getFieldValue('project_ids') || []
    if (projectIds.length === 0) {
      message.warning('请先选择项目')
      return
    }
    try {
      const result = await outreachApi.applyStrategy({
        project_ids: projectIds,
        strategy: params.strategy,
        channels: params.channels,
        intervals: params.intervals,
      })
      message.success(`已创建 ${result.created} 个任务`)
      setStrategyModalOpen(false)
      refetch()
    } catch (error: any) {
      message.error(`策略应用失败: ${error.message}`)
    }
  }

  // P1-2: 打开项目时间线
  const openProjectTimeline = (projectId: string) => {
    setTimelineProjectId(projectId)
    setTimelineDrawerOpen(true)
  }

  // 队列状态显示
  const totalPending =
    (queueStatus?.email?.pending || 0) +
    (queueStatus?.twitter?.pending || 0) +
    (queueStatus?.telegram?.pending || 0)
  const totalProcessing =
    (queueStatus?.email?.processing || 0) +
    (queueStatus?.twitter?.processing || 0) +
    (queueStatus?.telegram?.processing || 0)

  return (
    <div className="fade-in">
      {/* 页面标题 */}
      <div className="page-header">
        <h1>触达中心</h1>
        <Space>
          <Button
            icon={isPaused ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
            onClick={handleTogglePause}
          >
            {isPaused ? '恢复队列' : '暂停队列'}
          </Button>
          <Button type="primary" icon={<SendOutlined />} onClick={() => setTaskModalOpen(true)}>
            创建触达任务
          </Button>
        </Space>
      </div>

      {/* Twitter API 状态告警 */}
      {twitterStatus?.error && (
        <Alert
          message="Twitter API 异常"
          description={twitterStatus.error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}
      {twitterStatus?.rate_limited && !twitterStatus?.error && (
        <Alert
          message="Twitter API 限流中"
          description={`当前 Twitter API 处于冷却期，剩余约 ${Math.ceil((twitterStatus.rate_limit_remaining_seconds || 0) / 60)} 分钟。冷却结束后系统将自动重发待发送的任务。`}
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {/* 队列状态 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontWeight: 600 }}>触达队列</span>
          <Button size="small" icon={<ReloadOutlined />} onClick={() => { refetchQueueStatus(); loadEtQueue(); }}>
            刷新进度
          </Button>
        </div>
        <Row gutter={[12, 12]}>
          <Col xs={12} md={4}>
            <Statistic title="待发送" value={totalPending + (etQueueStatus?.total_queued || 0)} prefix={<SendOutlined />} valueStyle={{ fontSize: 20 }} />
          </Col>
          <Col xs={12} md={4}>
            <Statistic title="处理中" value={totalProcessing + (etQueueStatus?.total_sending || 0)} valueStyle={{ fontSize: 20 }} />
          </Col>
          {/* Email 队列 */}
          <Col xs={12} md={5}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <span>Email 队列</span>
              <Progress
                percent={(() => { const q = etQueueStatus?.channels?.email; const t = (q?.queued || 0) + (q?.sending || 0); return t > 0 ? Math.round(((q?.sending || 0) / t) * 100) : 0 })()}
                size="small"
                format={() => { const q = etQueueStatus?.channels?.email; return `${q?.sending || 0}/${(q?.queued || 0) + (q?.sending || 0)}` }}
              />
              {etQueueStatus?.channels?.email?.sent_today > 0 && (
                <span style={{ fontSize: 11, color: '#999' }}>今日已发 {etQueueStatus.channels.email.sent_today}</span>
              )}
            </Space>
          </Col>
          {/* Twitter 队列 */}
          <Col xs={12} md={5}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <span>Twitter 队列（共享）</span>
              <Progress
                percent={(() => { const p = (queueStatus as any)?.twitter?.pending || 0; const pr = (queueStatus as any)?.twitter?.processing || 0; const t = p + pr; return t > 0 ? Math.round((pr / t) * 100) : 0 })()}
                size="small"
                format={() => { const p = (queueStatus as any)?.twitter?.pending || 0; const pr = (queueStatus as any)?.twitter?.processing || 0; return `${pr}/${p + pr}` }}
              />
            </Space>
          </Col>
          {/* Telegram 队列 */}
          <Col xs={12} md={6}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <span>Telegram 队列</span>
              <Progress
                percent={(() => { const chs = etQueueStatus?.channels || {}; let q = 0, s = 0; for (const k of Object.keys(chs)) { if (k.startsWith('telegram')) { q += chs[k].queued || 0; s += chs[k].sending || 0 } } const t = q + s; return t > 0 ? Math.round((s / t) * 100) : 0 })()}
                size="small"
                format={() => { const chs = etQueueStatus?.channels || {}; let q = 0, s = 0; for (const k of Object.keys(chs)) { if (k.startsWith('telegram')) { q += chs[k].queued || 0; s += chs[k].sending || 0 } } return `${s}/${q + s}` }}
              />
              {(() => { const chs = etQueueStatus?.channels || {}; let st = 0; for (const k of Object.keys(chs)) { if (k.startsWith('telegram')) st += chs[k].sent_today || 0 } return st > 0 ? <span style={{ fontSize: 11, color: '#999' }}>今日已发 {st}</span> : null })()}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* ============ 触达管理（待发送=项目列表 / 已发送&失败=触达记录） ============ */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            value={prjChannel}
            onChange={(v) => { setPrjChannel(v); setPrjPage(1); setPrjSelectedKeys([]) }}
            style={{ width: 160 }}
          >
            <Select.Option value="email">Email</Select.Option>
            <Select.Option value="twitter_dm">Twitter DM</Select.Option>
            <Select.Option value="telegram_group">TG 群消息</Select.Option>
            <Select.Option value="telegram_dm">TG 群主私信</Select.Option>
            <Select.Option value="telegram_admin_dm">TG 管理员私信</Select.Option>
          </Select>
          <Select
            value={prjSendStatus}
            onChange={(v) => { setPrjSendStatus(v); setPrjPage(1); setPrjSelectedKeys([]); setSelectedRecordKeys([]) }}
            style={{ width: 120 }}
          >
            <Select.Option value="unsent">待发送</Select.Option>
            <Select.Option value="sent">已发送</Select.Option>
            <Select.Option value="failed">失败</Select.Option>
          </Select>
          {prjSendStatus === 'unsent' && (
            <Select
              value={prjInfoComplete}
              onChange={(v) => { setPrjInfoComplete(v); setPrjPage(1); setPrjSelectedKeys([]) }}
              style={{ width: 140 }}
            >
              <Select.Option value="complete">信息完整</Select.Option>
              <Select.Option value="incomplete">信息不完整</Select.Option>
              <Select.Option value="all">全部</Select.Option>
            </Select>
          )}
          <Input.Search
            placeholder="搜索项目"
            allowClear
            style={{ width: 200 }}
            onSearch={(v) => { setPrjSearch(v); setPrjPage(1) }}
          />
          {prjSendStatus === 'unsent' && prjInfoComplete === 'complete' && (
            <Button
              type="primary"
              icon={<SendOutlined />}
              disabled={prjSelectedKeys.length === 0}
              loading={prjSending}
              onClick={handleBatchSendProjects}
            >
              批量发送 ({prjSelectedKeys.length})
            </Button>
          )}
          {prjSendStatus === 'failed' && (
            <Button icon={<RetweetOutlined />} onClick={handleRetryAllFailed} danger>
              重试所有失败
            </Button>
          )}
          <Button icon={<ReloadOutlined />} onClick={() => { loadOutreachProjects(); refetch() }}>刷新</Button>
        </Space>
      </Card>

      {/* 表格：待发送=项目列表 / 已发送&失败=触达记录 */}
      <Card>
        {prjSendStatus === 'unsent' ? (
          /* 待发送：显示项目列表，可多选批量发送 */
          <Table
            rowKey="id"
            size="small"
            loading={prjLoading}
            dataSource={prjData.items}
            scroll={{ x: 1000 }}
            rowSelection={prjInfoComplete === 'complete' ? {
              selectedRowKeys: prjSelectedKeys,
              onChange: setPrjSelectedKeys,
              selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
            } : undefined}
            pagination={{
              current: prjPage, pageSize: 50, total: prjData.total,
              showTotal: (t) => `${t} 个项目`,
              onChange: (p) => setPrjPage(p),
            }}
            columns={[
              { title: '项目', dataIndex: 'name', key: 'name', width: 200,
                render: (name: string, r: any) => <a onClick={() => navigate(`/projects/${r.id}`)}>{name}</a> },
              { title: '网站', dataIndex: 'website', key: 'website', width: 200, ellipsis: true },
              ...(prjChannel === 'email' ? [
                { title: '邮箱', dataIndex: 'email', key: 'email', width: 220, render: (v: string) => v || <Tag color="red">缺失</Tag> },
              ] : []),
              ...(prjChannel === 'twitter_dm' ? [
                { title: 'Twitter', dataIndex: 'twitter_handle', key: 'twitter', width: 160, render: (v: string) => v ? `@${v}` : <Tag color="red">缺失</Tag> },
              ] : []),
              ...(prjChannel.startsWith('telegram') ? [
                { title: 'TG群组', dataIndex: 'telegram_group', key: 'tg', width: 200, ellipsis: true, render: (v: string) => v || <Tag color="red">缺失</Tag> },
                { title: '管理员', key: 'admins', width: 180, render: (_: any, r: any) => {
                  const admins = r.telegram_admins || []
                  return admins.length > 0
                    ? admins.slice(0, 3).map((a: any, i: number) => <Tag key={i} color="purple" style={{ fontSize: 11 }}>@{a.username}</Tag>)
                    : <Tag color="red">无</Tag>
                }},
              ] : []),
              { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160,
                render: (v: string) => v ? new Date(v).toLocaleString('zh-CN') : '-' },
            ]}
          />
        ) : (
          /* 已发送/失败：显示触达记录列表 */
          <Table
            columns={columns}
            dataSource={data?.items}
            rowKey="id"
            loading={isLoading}
            scroll={{ x: 1400 }}
            rowSelection={{
              selectedRowKeys: selectedRecordKeys,
              onChange: setSelectedRecordKeys,
            }}
            pagination={{
              current: page, pageSize,
              total: data?.total,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (p, ps) => {
                setFilter('page', p)
                if (ps !== pageSize) setFilter('pageSize', ps)
              },
            }}
          />
        )}
      </Card>

      {/* 创建任务弹窗 */}
      <Modal
        title="创建触达任务"
        open={taskModalOpen}
        onCancel={() => {
          setTaskModalOpen(false)
          form.resetFields()
        }}
        onOk={() => form.submit()}
        confirmLoading={createTask.isPending}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateTask}
          initialValues={{
            project_ids: [],  // 由useEffect处理URL参数
            channel: 'email',
            phase: 1,
          }}
        >
          <Form.Item
            name="project_ids"
            label="选择项目"
            rules={[{ required: true, message: '请选择至少一个项目' }]}
          >
            <Select
              mode="multiple"
              placeholder="选择要触达的项目"
              showSearch
              optionFilterProp="label"
              options={projectsData?.items?.map((p) => ({
                value: p.id,
                label: p.name,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="channel"
            label="触达渠道"
            rules={[{ required: true }]}
          >
            <Select
              options={Object.entries(channelConfig).map(([key, value]) => ({
                value: key,
                label: (
                  <Space>
                    {value.icon}
                    {value.text}
                  </Space>
                ),
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 触达详情弹窗 */}
      <Modal
        title="触达详情"
        open={detailModalOpen}
        onCancel={() => {
          setDetailModalOpen(false)
          setSelectedRecord(null)
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalOpen(false)}>
            关闭
          </Button>,
          ...(selectedRecord?.status === 'pending' ? [
            <Button
              key="send"
              type="primary"
              icon={<SendOutlined />}
              onClick={() => {
                if (selectedRecord) {
                  sendOutreach.mutate({
                    project_id: selectedRecord.project_id,
                    channel: selectedRecord.channel,
                    phase: selectedRecord.phase,
                  })
                  setDetailModalOpen(false)
                }
              }}
            >
              立即发送
            </Button>
          ] : []),
          ...(selectedRecord?.status === 'failed' ? [
            <Button
              key="retry"
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() => {
                if (selectedRecord) {
                  retryOutreach.mutate(selectedRecord.id)
                  setDetailModalOpen(false)
                }
              }}
            >
              重试发送
            </Button>
          ] : []),
        ]}
        width={700}
      >
        {selectedRecord && (
          <div style={{ lineHeight: 2 }}>
            <Row gutter={[16, 8]}>
              <Col span={8}>
                <strong>项目：</strong>
                <a onClick={() => navigate(`/projects/${selectedRecord.project_id}`)}>
                  {selectedRecord.project?.name || `项目 ${selectedRecord.project_id}`}
                </a>
              </Col>
              <Col span={8}>
                <strong>渠道：</strong>
                <Tag
                  icon={channelConfig[selectedRecord.channel]?.icon}
                  color={channelConfig[selectedRecord.channel]?.color}
                >
                  {channelConfig[selectedRecord.channel]?.text || selectedRecord.channel}
                </Tag>
              </Col>
              <Col span={8}>
                <strong>阶段：</strong> {(() => {
                  const pm: Record<string, number> = { email: 1, twitter_dm: 2, twitter_reply: 2, telegram_group: 3, telegram_dm: 3, telegram_admin_dm: 3 }
                  return `Phase ${pm[selectedRecord.channel] ?? '-'}`
                })()}
              </Col>
              <Col span={8}>
                <strong>状态：</strong>
                <Tag color={statusConfig[selectedRecord.status]?.color}>
                  {statusConfig[selectedRecord.status]?.text || selectedRecord.status}
                </Tag>
              </Col>
              <Col span={8}>
                <strong>创建时间：</strong>
                {dayjs(selectedRecord.created_at).format('YYYY-MM-DD HH:mm')}
              </Col>
              <Col span={8}>
                <strong>发送时间：</strong>
                {selectedRecord.sent_at
                  ? dayjs(selectedRecord.sent_at).format('YYYY-MM-DD HH:mm')
                  : '-'}
              </Col>
              <Col span={8}>
                <strong>重试次数：</strong> {selectedRecord.retry_count || 0}
              </Col>
              {selectedRecord.opened_at && (
                <Col span={8}>
                  <strong>打开时间：</strong>
                  {dayjs(selectedRecord.opened_at).format('YYYY-MM-DD HH:mm')}
                </Col>
              )}
              {selectedRecord.replied_at && (
                <Col span={8}>
                  <strong>回复时间：</strong>
                  {dayjs(selectedRecord.replied_at).format('YYYY-MM-DD HH:mm')}
                </Col>
              )}
            </Row>

            {selectedRecord.subject && (
              <div style={{ marginTop: 16 }}>
                <strong>主题：</strong>
                <div
                  style={{
                    background: '#f5f5f5',
                    padding: '8px 12px',
                    borderRadius: 4,
                    marginTop: 4,
                  }}
                >
                  {selectedRecord.subject}
                </div>
              </div>
            )}

            <div style={{ marginTop: 16 }}>
              <strong>内容：</strong>
              <div
                style={{
                  background: '#f5f5f5',
                  padding: '12px 16px',
                  borderRadius: 4,
                  marginTop: 4,
                  maxHeight: 300,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {selectedRecord.content || '(无内容)'}
              </div>
            </div>

            {selectedRecord.error_message && (
              <div style={{ marginTop: 16 }}>
                <strong style={{ color: '#ff4d4f' }}>错误信息：</strong>
                <div
                  style={{
                    background: '#fff2f0',
                    padding: '12px 16px',
                    borderRadius: 4,
                    marginTop: 4,
                    color: '#ff4d4f',
                    border: '1px solid #ffccc7',
                  }}
                >
                  {selectedRecord.error_message}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 缺失信息提示弹窗 */}
      <MissingInfoModal
        visible={missingInfoModalOpen}
        data={missingInfoData}
        onClose={() => setMissingInfoModalOpen(false)}
        onRefresh={() => refetch()}
      />

      {/* 项目详情抽屉 */}
      <ProjectInfoDrawer
        projectId={selectedProjectForInfo}
        visible={projectInfoDrawerOpen}
        onClose={() => {
          setProjectInfoDrawerOpen(false)
          setSelectedProjectForInfo(null)
        }}
      />

      {/* P0-1: 一步式发送弹窗 */}
      <Modal
        title="一步式发送"
        open={createAndSendModalOpen}
        onCancel={() => {
          setCreateAndSendModalOpen(false)
          createAndSendForm.resetFields()
        }}
        onOk={() => createAndSendForm.submit()}
        okText="立即发送"
        width={600}
      >
        <Alert
          message="选择项目和渠道后直接发送，无需先创建任务再手动点发送。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={createAndSendForm} layout="vertical" onFinish={handleCreateAndSend}>
          <Form.Item name="project_ids" label="选择项目" rules={[{ required: true, message: '请选择项目' }]}>
            <Select
              mode="multiple"
              placeholder="选择要触达的项目"
              showSearch
              optionFilterProp="label"
              options={projectsData?.items?.map((p) => ({ value: p.id, label: p.name }))}
            />
          </Form.Item>
          <Form.Item name="channel" label="触达渠道" rules={[{ required: true }]} initialValue="email">
            <Select
              options={Object.entries(channelConfig).map(([key, value]) => ({
                value: key,
                label: <Space>{value.icon}{value.text}</Space>,
              }))}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="dashed"
              icon={<AppstoreOutlined />}
              onClick={() => setStrategyModalOpen(true)}
              block
            >
              或使用策略模板批量编排
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default OutreachCenter
